import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Modal from '@components/common/Modal/Modal'
import projectApprovalService from '@services/projectApprovalService'
import './ExecutiveReviewModal.css'

/**
 * ExecutiveReviewModal Component
 *
 * Modal for Executive to review excused checklist items and decide keep/remove
 *
 * @param {boolean} isOpen - Modal open state
 * @param {Function} onClose - Close modal callback
 * @param {number} projectId - Project ID
 * @param {Function} onSuccess - Success callback after review submission
 */
const ExecutiveReviewModal = ({ isOpen, onClose, projectId, onSuccess }) => {
  const [excusedItems, setExcusedItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [decisions, setDecisions] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen) {
      fetchExcusedItems()
    }
  }, [isOpen, projectId])

  const fetchExcusedItems = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await projectApprovalService.getExcusedItems(projectId)
      setExcusedItems(response.data || [])
      setDecisions({})
    } catch (err) {
      setError('Failed to load excused items')
    } finally {
      setLoading(false)
    }
  }

  const handleDecision = (checklistId, decision, notes = '') => {
    setDecisions((prev) => ({
      ...prev,
      [checklistId]: { decision, notes },
    }))
  }

  const handleSubmit = async () => {
    // Validate all items are reviewed
    if (Object.keys(decisions).length !== excusedItems.length) {
      setError('Please make a decision on all excused items')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Convert decisions to API format
      const apiDecisions = Object.entries(decisions).map(([checklistId, decision]) => ({
        checklist_id: parseInt(checklistId),
        decision: decision.decision,
        ...(decision.notes && { notes: decision.notes }),
      }))

      const response = await projectApprovalService.executiveReview(projectId, apiDecisions)

      // Success
      if (onSuccess) {
        onSuccess(response)
      }
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit review')
    } finally {
      setSubmitting(false)
    }
  }

  const summary = {
    total: excusedItems.length,
    keep: Object.values(decisions).filter((d) => d.decision === 'keep').length,
    remove: Object.values(decisions).filter((d) => d.decision === 'remove').length,
    pending: excusedItems.length - Object.keys(decisions).length,
  }

  const allReviewed = Object.keys(decisions).length === excusedItems.length

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnBackdrop={false}>
      <div className="executive-review-modal">
        <div className="executive-review-modal__header">
          <h2>Executive Review - Excused Items</h2>
          <p className="executive-review-modal__subtitle">
            Decide whether to keep or remove items excused by the Project Manager
          </p>
        </div>

        {loading ? (
          <div className="executive-review-modal__loading">Loading excused items...</div>
        ) : (
          <>
            <div className="executive-review-modal__summary">
              <span className="executive-review-modal__summary-item executive-review-modal__summary-item--total">
                Total: {summary.total}
              </span>
              <span className="executive-review-modal__summary-item executive-review-modal__summary-item--keep">
                Keep: {summary.keep}
              </span>
              <span className="executive-review-modal__summary-item executive-review-modal__summary-item--remove">
                Remove: {summary.remove}
              </span>
              <span className="executive-review-modal__summary-item executive-review-modal__summary-item--pending">
                Pending: {summary.pending}
              </span>
            </div>

            {error && (
              <div className="executive-review-modal__error">
                <span className="executive-review-modal__error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <div className="executive-review-modal__items">
              {excusedItems.length === 0 ? (
                <div className="executive-review-modal__empty">No excused items to review</div>
              ) : (
                excusedItems.map((item, index) => (
                  <ExcusedItemReview
                    key={item.id}
                    item={item}
                    index={index}
                    decision={decisions[item.id]}
                    onDecision={(decision, notes) => handleDecision(item.id, decision, notes)}
                    disabled={submitting}
                  />
                ))
              )}
            </div>

            <div className="executive-review-modal__footer">
              <button className="btn btn--secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button
                className="btn btn--primary"
                onClick={handleSubmit}
                disabled={!allReviewed || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Decisions'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

/**
 * ExcusedItemReview Sub-component
 */
const ExcusedItemReview = ({ item, index, decision, onDecision, disabled }) => {
  const [notes, setNotes] = useState('')
  const [showNotes, setShowNotes] = useState(false)

  const handleKeep = () => {
    if (showNotes && notes.trim()) {
      onDecision('keep', notes)
      setShowNotes(false)
    } else if (!showNotes) {
      setShowNotes(true)
    } else {
      onDecision('keep', '')
    }
  }

  const handleRemove = () => {
    if (showNotes && notes.trim()) {
      onDecision('remove', notes)
      setShowNotes(false)
    } else if (!showNotes) {
      setShowNotes(true)
    } else {
      onDecision('remove', '')
    }
  }

  const isKept = decision?.decision === 'keep'
  const isRemoved = decision?.decision === 'remove'

  return (
    <div
      className={`excused-item ${isKept ? 'excused-item--kept' : ''} ${isRemoved ? 'excused-item--removed' : ''}`}
    >
      <div className="excused-item__header">
        <div className="excused-item__index">{index + 1}</div>
        <div className="excused-item__content">
          <h4 className="excused-item__title">{item.title}</h4>
          {item.description && <p className="excused-item__description">{item.description}</p>}
        </div>
      </div>

      <div className="excused-item__excuse-info">
        <strong>PM Excuse Reason:</strong>
        <p>{item.excuse_reason}</p>
        {item.first_name && (
          <small>
            Excused by: {item.first_name} {item.last_name}
          </small>
        )}
      </div>

      <div className="excused-item__actions">
        <button
          className={`btn btn--sm ${isKept ? 'btn--success' : 'btn--outline-success'}`}
          onClick={handleKeep}
          disabled={disabled}
        >
          {isKept ? '✓ Kept' : 'Keep Item'}
        </button>

        <button
          className={`btn btn--sm ${isRemoved ? 'btn--danger' : 'btn--outline-danger'}`}
          onClick={handleRemove}
          disabled={disabled}
        >
          {isRemoved ? '✗ Removed' : 'Remove Item'}
        </button>
      </div>

      {(showNotes || decision) && (
        <div className="excused-item__notes">
          <label>Executive Notes (optional):</label>
          <textarea
            value={decision ? decision.notes : notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes for your decision..."
            rows={2}
            disabled={disabled || !!decision}
          />
          {!decision && (
            <div className="excused-item__notes-actions">
              <button
                className="btn btn--sm btn--success"
                onClick={handleKeep}
                disabled={disabled}
              >
                Confirm Keep
              </button>
              <button
                className="btn btn--sm btn--danger"
                onClick={handleRemove}
                disabled={disabled}
              >
                Confirm Remove
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

ExcusedItemReview.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  decision: PropTypes.object,
  onDecision: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

ExecutiveReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
}

export default ExecutiveReviewModal
